import { useEffect } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, IconButton, Autocomplete, Box, Checkbox, FormControlLabel, Divider, Typography,
} from '@mui/material'
import { Add, DeleteOutlined, ArrowUpward, ArrowDownward } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createRoute, updateRoute, getRoute, type RouteListItem, type SaveRoutePayload, type ProductionOperationType,
} from '../../api/productieRute'
import { listArticles } from '../../api/nomenclatoare'
import { listEquipment } from '../../api/echipamente'
import { errorMessage } from '../../api/client'

const OPERATION_TYPE_LABEL: Record<ProductionOperationType, string> = {
  AlimentareFolie: 'Alimentare folie', Incalzire: 'Încălzire', Formare: 'Formare', Racire: 'Răcire',
  TaiereStantare: 'Tăiere / Ștanțare', Stivuire: 'Stivuire', ControlVizual: 'Control vizual',
  Ambalare: 'Ambalare', Paletizare: 'Paletizare', Altele: 'Altele',
}

const operationSchema = z.object({
  type: z.enum(['AlimentareFolie', 'Incalzire', 'Formare', 'Racire', 'TaiereStantare', 'Stivuire', 'ControlVizual', 'Ambalare', 'Paletizare', 'Altele']),
  name: z.string().min(1, 'Câmp obligatoriu'),
  setupTimeMinutes: z.string().optional(),
  cycleTimeSecondsPerUnit: z.string().optional(),
  expectedYieldPercent: z.string().optional(),
  requiresQualityCheckpoint: z.boolean(),
  allowsOverlapWithNext: z.boolean(),
  wipArticleId: z.string(),
  compatibleEquipmentIds: z.array(z.string()),
})
const schema = z.object({
  productArticleId: z.string().min(1, 'Selectați produsul finit'),
  notes: z.string().optional(),
  operations: z.array(operationSchema).min(1, 'Adăugați cel puțin o operație'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { productArticleId: '', notes: '', operations: [] }
const EMPTY_OPERATION = {
  type: 'Formare' as ProductionOperationType, name: '', setupTimeMinutes: '', cycleTimeSecondsPerUnit: '',
  expectedYieldPercent: '', requiresQualityCheckpoint: false, allowsOverlapWithNext: false, wipArticleId: '',
  compatibleEquipmentIds: [] as string[],
}

export default function RouteFormDialog({ target, onClose }: { target: RouteListItem | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: equipment } = useQuery({ queryKey: ['mentenanta', 'echipamente', 'utilaje'], queryFn: listEquipment, enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['productie', 'rute', editing ? (target as RouteListItem).id : null],
    queryFn: () => getRoute((target as RouteListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const operations = useFieldArray({ control, name: 'operations' })

  useEffect(() => {
    if (editing && detail) {
      reset({
        productArticleId: detail.productArticleId, notes: detail.notes ?? '',
        operations: detail.operations.map((o) => ({
          type: o.type, name: o.name, setupTimeMinutes: o.setupTimeMinutes != null ? String(o.setupTimeMinutes) : '',
          cycleTimeSecondsPerUnit: o.cycleTimeSecondsPerUnit != null ? String(o.cycleTimeSecondsPerUnit) : '',
          expectedYieldPercent: o.expectedYieldPercent != null ? String(o.expectedYieldPercent) : '',
          requiresQualityCheckpoint: o.requiresQualityCheckpoint, allowsOverlapWithNext: o.allowsOverlapWithNext,
          wipArticleId: o.wipArticleId ?? '',
          compatibleEquipmentIds: o.compatibleEquipment.map((e) => e.equipmentId),
        })),
      })
    } else if (target === 'new') {
      reset(EMPTY)
    }
  }, [target, editing, detail, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveRoutePayload = {
        productArticleId: values.productArticleId, notes: values.notes || null,
        operations: values.operations.map((o, i) => ({
          sequenceOrder: i + 1, type: o.type, name: o.name,
          setupTimeMinutes: o.setupTimeMinutes ? Number(o.setupTimeMinutes) : null,
          cycleTimeSecondsPerUnit: o.cycleTimeSecondsPerUnit ? Number(o.cycleTimeSecondsPerUnit) : null,
          expectedYieldPercent: o.expectedYieldPercent ? Number(o.expectedYieldPercent) : null,
          requiresQualityCheckpoint: o.requiresQualityCheckpoint, allowsOverlapWithNext: o.allowsOverlapWithNext,
          wipArticleId: o.wipArticleId || null,
          compatibleEquipmentIds: o.compatibleEquipmentIds,
        })),
      }
      if (editing) return updateRoute((target as RouteListItem).id, payload)
      return createRoute(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['productie', 'rute'] })
      onClose()
    },
  })

  const readOnly = editing && detail && detail.status !== 'Draft'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? `Rută ${detail?.number}` : 'Rută nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {readOnly && <Alert severity="info">Doar rutele în ciornă pot fi editate — pentru o schimbare, arhivați ruta și creați una nouă.</Alert>}
            <Controller name="productArticleId" control={control} render={({ field }) => (
              <Autocomplete
                disabled={!!readOnly} options={articles?.filter((a) => a.type !== 'Set').map((a) => a.id) ?? []}
                getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                renderInput={(params) => <TextField {...params} label="Produs finit" error={!!errors.productArticleId} helperText={errors.productArticleId?.message} />}
              />
            )} />
            <Controller name="notes" control={control} render={({ field }) => (
              <TextField {...field} disabled={!!readOnly} label="Note" fullWidth multiline minRows={2} />
            )} />

            <Divider />
            <Typography variant="subtitle2">Operații (în ordine)</Typography>
            {typeof errors.operations?.message === 'string' && <Alert severity="warning">{errors.operations.message}</Alert>}
            {operations.fields.map((f, i) => (
              <Stack key={f.id} spacing={1} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                  <Typography variant="body2" sx={{ mt: 1.5, width: 24 }}>{i + 1}.</Typography>
                  <Controller name={`operations.${i}.type`} control={control} render={({ field }) => (
                    <TextField {...field} disabled={!!readOnly} select label="Tip" sx={{ width: 200 }} error={!!errors.operations?.[i]?.type}
                      helperText={errors.operations?.[i]?.type?.message}>
                      {Object.entries(OPERATION_TYPE_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                    </TextField>
                  )} />
                  <Controller name={`operations.${i}.name`} control={control} render={({ field }) => (
                    <TextField {...field} disabled={!!readOnly} label="Denumire" sx={{ flexGrow: 1 }} error={!!errors.operations?.[i]?.name}
                      helperText={errors.operations?.[i]?.name?.message} />
                  )} />
                  {!readOnly && (
                    <>
                      <IconButton disabled={i === 0} onClick={() => operations.swap(i, i - 1)} sx={{ mt: 0.5 }}><ArrowUpward fontSize="small" /></IconButton>
                      <IconButton disabled={i === operations.fields.length - 1} onClick={() => operations.swap(i, i + 1)} sx={{ mt: 0.5 }}><ArrowDownward fontSize="small" /></IconButton>
                      <IconButton onClick={() => operations.remove(i)} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
                    </>
                  )}
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <Controller name={`operations.${i}.setupTimeMinutes`} control={control} render={({ field }) => (
                    <TextField {...field} disabled={!!readOnly} type="number" label="Setup (min)" sx={{ width: 130 }} />
                  )} />
                  <Controller name={`operations.${i}.cycleTimeSecondsPerUnit`} control={control} render={({ field }) => (
                    <TextField {...field} disabled={!!readOnly} type="number" label="Timp unitar (s)" sx={{ width: 140 }} />
                  )} />
                  <Controller name={`operations.${i}.expectedYieldPercent`} control={control} render={({ field }) => (
                    <TextField {...field} disabled={!!readOnly} type="number" label="Randament %" sx={{ width: 130 }} />
                  )} />
                  <Controller name={`operations.${i}.requiresQualityCheckpoint`} control={control} render={({ field }) => (
                    <FormControlLabel sx={{ mt: 0.5 }} control={<Checkbox checked={field.value} disabled={!!readOnly} onChange={(e) => field.onChange(e.target.checked)} />} label="Punct control calitate" />
                  )} />
                  <Controller name={`operations.${i}.allowsOverlapWithNext`} control={control} render={({ field }) => (
                    <FormControlLabel sx={{ mt: 0.5 }} control={<Checkbox checked={field.value} disabled={!!readOnly} onChange={(e) => field.onChange(e.target.checked)} />} label="Poate rula suprapus cu următoarea" />
                  )} />
                </Stack>
                <Controller name={`operations.${i}.compatibleEquipmentIds`} control={control} render={({ field }) => (
                  <Autocomplete
                    multiple disabled={!!readOnly} options={equipment?.map((e) => e.id) ?? []}
                    getOptionLabel={(id) => { const e = equipment?.find((x) => x.id === id); return e ? `${e.code} — ${e.name}` : id }}
                    value={field.value} onChange={(_, v) => field.onChange(v)}
                    renderInput={(params) => <TextField {...params} label="Utilaje compatibile (opțional)" />}
                  />
                )} />
                <Controller name={`operations.${i}.wipArticleId`} control={control} render={({ field }) => (
                  <Autocomplete
                    disabled={!!readOnly} options={articles?.filter((a) => a.type === 'Semifabricat').map((a) => a.id) ?? []}
                    getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                    value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                    renderInput={(params) => <TextField {...params} label="Articol WIP (opțional — FR-OPE-026)" />}
                  />
                )} />
              </Stack>
            ))}
            {!readOnly && (
              <Box>
                <Button size="small" startIcon={<Add />} onClick={() => operations.append(EMPTY_OPERATION)}>Adaugă operație</Button>
              </Box>
            )}

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          {!readOnly && (
            <Button type="submit" variant="contained" disabled={mutation.isPending}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
              Salvează
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  )
}
